import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Position } from '../../helpers/types';
import { MapMarkerService, GoogleMarkerOptions, MapEventsService } from '../../_services/maps';

declare var google: any;

@Component({
  selector: 'app-map-marker',
  template: '', // Ce composant n'a pas de template car il gère seulement les marqueurs
  standalone: false,
})
export class MapMarkerComponent implements OnInit, OnDestroy {
  @Input() position!: Position;
  @Input() map!: google.maps.Map;
  @Input() options: Partial<GoogleMarkerOptions> = {};
  @Input() enableClick: boolean = true;
  @Input() enableDrag: boolean = false;
  @Input() customIcon?: string;
  @Input() iconSize: number = 40;
  @Input() title?: string;
  @Input() zIndex?: number;

  @Output() markerClick = new EventEmitter<{ marker: google.maps.Marker; position: Position }>();
  @Output() markerDrag = new EventEmitter<{ marker: google.maps.Marker; position: Position }>();
  @Output() markerDragEnd = new EventEmitter<{ marker: google.maps.Marker; position: Position }>();

  private marker: google.maps.Marker | null = null;
  private markerId: string = '';

  constructor(
    private mapMarkerService: MapMarkerService,
    private mapEventsService: MapEventsService
  ) {}

  ngOnInit(): void {
    this.createMarker();
  }

  ngOnDestroy(): void {
    this.removeMarker();
  }

  /**
   * Crée le marqueur
   */
  private createMarker(): void {
    if (!this.map || !this.position) {
      console.warn('Map or position not provided for marker');
      return;
    }

    const markerOptions: Partial<GoogleMarkerOptions> = {
      ...this.options,
      draggable: this.enableDrag,
      title: this.title || this.position.label || 'Marker',
      zIndex: this.zIndex,
    };

    // Créer le marqueur avec icône personnalisée si fournie
    if (this.customIcon) {
      this.marker = this.mapMarkerService.createCustomSVGMarker(
        this.position,
        this.map,
        this.customIcon,
        this.iconSize
      );
    } else {
      this.marker = this.mapMarkerService.createMarkerFromPosition(
        this.position,
        this.map,
        markerOptions
      );
    }

    if (this.marker) {
      this.markerId = this.generateMarkerId();
      this.setupMarkerEvents();
    }
  }

  /**
   * Configure les événements du marqueur
   */
  private setupMarkerEvents(): void {
    if (!this.marker) return;

    if (this.enableClick) {
      this.marker.addListener('click', () => {
        this.markerClick.emit({
          marker: this.marker!,
          position: this.position
        });
      });
    }

    if (this.enableDrag) {
      this.marker.addListener('drag', () => {
        const newPosition = this.getMarkerPosition();
        this.markerDrag.emit({
          marker: this.marker!,
          position: newPosition
        });
      });

      this.marker.addListener('dragend', () => {
        const newPosition = this.getMarkerPosition();
        this.markerDragEnd.emit({
          marker: this.marker!,
          position: newPosition
        });
      });
    }
  }

  /**
   * Obtient la position actuelle du marqueur
   */
  private getMarkerPosition(): Position {
    if (!this.marker) return this.position;

    const markerPosition = this.marker.getPosition();
    if (markerPosition) {
      return {
        label: this.position.label,
        coords: {
          latitude: markerPosition.lat(),
          longitude: markerPosition.lng(),
        },
      };
    }

    return this.position;
  }

  /**
   * Génère un ID unique pour le marqueur
   */
  private generateMarkerId(): string {
    return `marker-${this.position.coords.latitude}-${this.position.coords.longitude}-${Date.now()}`;
  }

  /**
   * Supprime le marqueur
   */
  private removeMarker(): void {
    if (this.marker) {
      this.mapMarkerService.removeMarker(this.marker);
      this.marker = null;
    }
  }

  /**
   * Met à jour la position du marqueur
   */
  public updatePosition(newPosition: Position): void {
    if (this.marker) {
      this.mapMarkerService.updateMarkerPosition(this.marker, newPosition);
      this.position = newPosition;
    }
  }

  /**
   * Met à jour l'icône du marqueur
   */
  public updateIcon(iconUrl: string, size?: number): void {
    if (this.marker) {
      this.mapMarkerService.updateMarkerIcon(this.marker, iconUrl, size);
    }
  }

  /**
   * Rend le marqueur visible ou invisible
   */
  public setVisible(visible: boolean): void {
    if (this.marker) {
      this.marker.setVisible(visible);
    }
  }

  /**
   * Anime le marqueur avec un rebond
   */
  public bounce(): void {
    if (this.marker) {
      this.marker.setAnimation(google.maps.Animation.BOUNCE);
      setTimeout(() => {
        if (this.marker) {
          this.marker.setAnimation(null);
        }
      }, 1400);
    }
  }

  /**
   * Obtient le marqueur
   */
  public getMarker(): google.maps.Marker | null {
    return this.marker;
  }

  /**
   * Obtient l'ID du marqueur
   */
  public getMarkerId(): string {
    return this.markerId;
  }
} 